import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { dashboardKeys } from './dashboard.queries';
import type { DroneStatus } from '../../../types/api';
import { useTranslation } from '../../../i18n';

// STD-015: write/imperative actions for the Dashboard domain. apiService is
// imported here (a *.mutations.ts file) per STD-016. Errors are toasted by the
// global MutationCache handler; meta.errorMessage is the fallback.

export function useUploadDronePictureMutation(droneSerial: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('dronePicture.failedToUpload') },
    mutationFn: async (file: File) => {
      const res = await apiService.uploadDronePicture(droneSerial, file);
      if (!res.success) throw new Error(res.message || 'Failed to upload picture');
      return res.picture_url;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.dronePictureUrl(droneSerial) });
    },
  });
}

export function useUpdateDroneStatusMutation(droneSerial: string) {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('droneStatus.failedToUpdate') },
    mutationFn: async (status: DroneStatus) => {
      const res = await apiService.updateDroneStatus(droneSerial, status);
      return res;
    },
  });
}

export function useUpdateDroneMetadataMutation(droneSerial: string) {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('technicalSpecs.failedToUpdate') },
    mutationFn: async (metadata: Record<string, unknown>) => {
      const res = await apiService.updateDroneMetadata(droneSerial, metadata);
      return res;
    },
  });
}

export function useMarkAnnouncementReadMutation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('inbox.failedToMarkRead') },
    mutationFn: async (recipientId: string) => {
      return apiService.markAnnouncementAsRead(recipientId);
    },
    onSuccess: () => {
      // Refresh the unread count badge after marking read
      queryClient.invalidateQueries({ queryKey: dashboardKeys.unreadAnnouncementCount() });
    },
  });
}

export function useDownloadAnnouncementAttachmentMutation() {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('inbox.failedToDownloadAttachment') },
    mutationFn: async (s3Key: string) => {
      const res = await apiService.getAnnouncementAttachmentDownloadUrl(s3Key);
      return res.url;
    },
  });
}
